import { Component, inject, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { AIService, Question } from "@bequeathed/ngx-services";
import { PageUtil, ThreadUtils } from "@bequeathed/ngx-utils";
import { ConfirmOptionControl } from "../control/confirm-option-control/confirm-option-control.component";
import { BaseForm } from "../base-form/base-form.component";
import { UntypedFormGroup } from "@angular/forms";
import { LoggerService, TokenService } from "@bequeathed/ngx-app-core";
import { Topic } from "@bequeathed/ngx-services/lib/model/ai-interview.model";
import { ActivatedRoute, Router } from "@angular/router";
import { CharityControl } from "../control/charity-control/charity-control.component";
import { CheckboxControl } from "../control/checkbox-control/checkbox-control.component";
import { TextControl } from "../control/text-control/text-control.component";
import { CharitySearchControl } from "../control/charity-search-control/charity-search-control.component";
import { VideoControl } from "../control/video-control/video-control.component";
import { DropdownControl } from "../control/dropdown-control/dropdown-control.component";
import { CurrencyControl } from "../control/currency-control/currency-control.component";
import { EmailControl } from "../control/email-control/email-control.component";
import { NumberControl } from "../control/number-control/number-control.component";
import { PercentageControl } from "../control/percentage-control/percentage-control.component";
import { PostcodeControl } from "../control/postcode-control/postcode-control.component";
import { RadioButtonControl } from "../control/radio-button-control/radio-button-control.component";
import { GroupControl } from "../control/group-control/group-control.component";
import {SummaryComponent} from "../../../features/interview/summary/summary.component";
import {QuestionUtils} from "../../utils/question.utils";

const LOG = LoggerService.get('EditQuestionPopupComponent');

@Component({
  selector: 'edit-question-popup',
  standalone: true,
    imports: [
        CharitySearchControl,
        CharityControl,
        CheckboxControl,
        ConfirmOptionControl,
        CurrencyControl,
        DropdownControl,
        EmailControl,
        NumberControl,
        PercentageControl,
        PostcodeControl,
        RadioButtonControl,
        TextControl,
        VideoControl,
        GroupControl,
    ],
  templateUrl: './edit-question-popup.component.html',
  styleUrl: './edit-question-popup.component.sass'
})
export class EditQuestionPopupComponent extends BaseForm implements OnInit {

    // @ts-expect-error Comes from the template
    @Input() public question: Question;
    // @ts-expect-error Comes from the template
    @Input() public topic: Topic;
    // @ts-expect-error Comes from the template
    @Input() public type: string;
    // @ts-expect-error Comes from the template
    @ViewChild('modal') private modalContent: TemplateRef<EditQuestionPopupComponent>;
    // @ts-expect-error Provided via template
    @Input() summary: SummaryComponent;

    // @ts-expect-error Comes from the open method
    public modalConfig: ModalConfig;
    // @ts-expect-error Comes from the open method
    private modalRef: NgbModalRef;

    aiService: AIService = inject(AIService);
    modalService: NgbModal = inject(NgbModal);
    tokenService: TokenService = inject(TokenService);
    route: ActivatedRoute = inject(ActivatedRoute);
    router: Router = inject(Router)

    // @ts-expect-error Check ngOnInit
    interviewId: number;

    override ngOnInit() {
        super.ngOnInit()

        this.route.params.subscribe((params: any) => {
            this.interviewId = params.id;
        });

    }

    private async initInterview(): Promise<void> {
        this.form = new UntypedFormGroup({});
        this.loading = true;

        const user = this.tokenService.getUser();

        const questionMap = new Map<string, any>([
            ['editTopicId', this.modalConfig.topic.id],
            ['editQuestionId', this.modalConfig.question.id],
        ]);
        const newState = await this.aiService.editSummaryQuestion(`${this.interviewId}`, `${user?.userId}`, questionMap);

        newState!.currentQuestion = this.modalConfig.question;
        newState!.navigator!.currentTopic = this.modalConfig.topic.id;

        this.interviewState.set(newState);
        QuestionUtils.setUserMetadata(this.interviewState()?.metadata)

        this.loading = false;

        await ThreadUtils.sleep(50);
        PageUtil.setFocus('topicTitle');
    }

    async open(config: ModalConfig): Promise<boolean> {
        if (config) {
            this.modalConfig = config;

            this.initInterview().then(() => LOG.debug(
                'ngOnInit',
                `InterviewState initialised for editing!
                 Topic ID: ${this.modalConfig.topic.id}
                 Question ID: ${this.modalConfig.question.id}`
            ));
        }
        PageUtil.setFocus('#edit-confirm-title', 500)
        return new Promise<boolean>(resolve => {
            this.modalRef = this.modalService.open(this.modalContent, { centered: true, windowClass: 'popup-window' });
            this.modalRef.result.then(resolve, resolve);
        })
    }

    triggerEdit(): () => void {
        return () => this.edit();
    }

    async edit(): Promise<void> {
        if (this.loading) {
            LOG.debug("edit", "Interview edit is already being evaluated, please wait...");
            return;
        }
        await this.revalidateForm();
        if (this.form.invalid || this.errorMap.size > 0) {
            LOG.debug('edit', `Form is still invalid Errors in form: ${this.errorMap.size}`);
            return;
        }

        this.loading = true;
        this.questionControl!.applyValue(this.interviewState()!);
        LOG.debug('edit', `New answer to be submitted: ${this.interviewState()?.currentQuestion?.userResponse}`);

        this.interviewState.set(await this.aiService.editQuestion(this.interviewState()!));
        QuestionUtils.setUserMetadata(this.interviewState()?.metadata)
        if (this.interviewState()?.currentQuestion) {
            LOG.debug('edit',
                `A new question has come in with ID: ${this.interviewState()?.currentQuestion!.id}

                Redirecting to [/interview/WILL/${this.interviewId}] to continue with the new thread of questions.
                `)
            await this.router.navigateByUrl(`/interview/WILL/${this.interviewId}`, { replaceUrl: true });
        }

        this.summary.updated.next(true);
        this.modalRef.close();

    }

}

export interface ModalConfig {

    question: Question,
    topic: Topic,
    dismissButtonLabel?: string;
    confirmButtonLabel?: string;

}
